import { useState } from "react";
import { Clock, Printer } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Separator,
} from "@/components/ui";
import {
  EmptyState,
  PageError,
  PageHeader,
  PageLoader,
} from "@/components/common";
import { formatCurrency, formatDateTime } from "@/lib/utils";
import { useCurrentShift, useShiftSummary } from "@/features/shifts/api";
import { useEndOfDayReport } from "../api";
import {
  BestSellersCard,
  CategoryBreakdownCard,
  CustomerInsightsCard,
  GiveawayCard,
  HourlyRevenueChart,
  KpiGrid,
  OrderTypeCard,
  PaymentMixCard,
} from "../components";

export function EndOfDayPage() {
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0],
  );

  const {
    data: report,
    isLoading,
    error,
    refetch,
  } = useEndOfDayReport(selectedDate);
  const { data: currentShift } = useCurrentShift();
  const { data: shiftSummary } = useShiftSummary(currentShift?.id || "");

  if (isLoading) {
    return <PageLoader />;
  }

  if (error || !report) {
    return (
      <PageError message="Failed to load end of day report" onRetry={refetch} />
    );
  }

  return (
    <div className="container mx-auto space-y-6 p-4 sm:p-6">
      <PageHeader
        title="End of Day"
        description={`Daily close-out summary for ${selectedDate}`}
        actions={
          <div className="flex gap-2">
            <Label htmlFor="date" className="sr-only">
              Date
            </Label>
            <Input
              id="date"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-40"
            />
            <Button variant="outline" onClick={() => window.print()}>
              <Printer className="mr-2 h-4 w-4" />
              Print
            </Button>
          </div>
        }
      />
      <KpiGrid report={report} />
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <HourlyRevenueChart
            data={report.revenueByHour}
            peakHour={report.peakHour}
          />
        </div>
        <CurrentShiftCard
          currentShift={currentShift}
          shiftSummary={shiftSummary}
        />
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <BestSellersCard items={report.topSellingProducts} />
        <CategoryBreakdownCard rows={report.categoryBreakdown} />
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <CustomerInsightsCard data={report.customers} />
        <PaymentMixCard data={report.salesByPaymentMethod} />
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <OrderTypeCard rows={report.orderTypeBreakdown} />
        <GiveawayCard
          data={report.giveaways}
          totalRevenue={report.totalRevenue}
        />
      </div>
    </div>
  );
}

interface CurrentShiftCardProps {
  currentShift: ReturnType<typeof useCurrentShift>["data"];
  shiftSummary: ReturnType<typeof useShiftSummary>["data"];
}

function CurrentShiftCard({
  currentShift,
  shiftSummary,
}: CurrentShiftCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Current shift
        </CardTitle>
        <CardDescription>
          {currentShift ? "Shift summary" : "No active shift"}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {currentShift ? (
          <div className="space-y-3 text-sm">
            <Row
              label="Opened by"
              value={
                currentShift.openedBy
                  ? `${currentShift.openedBy.firstName} ${currentShift.openedBy.lastName}`
                  : "—"
              }
            />
            <Row
              label="Opened at"
              value={formatDateTime(currentShift.openedAt)}
            />
            <Row
              label="Opening cash"
              value={formatCurrency(currentShift.openingCash)}
            />
            <Separator />
            {shiftSummary && (
              <>
                <Row
                  label="Orders this shift"
                  value={String(shiftSummary.totalOrders)}
                />
                <Row
                  label="Revenue this shift"
                  value={formatCurrency(shiftSummary.totalRevenue)}
                />
                <Row
                  label="Expected cash"
                  value={formatCurrency(shiftSummary.expectedCash)}
                  emphasis
                />
              </>
            )}
          </div>
        ) : (
          <EmptyState
            icon={Clock}
            title="No active shift"
            description="Open a shift to see live summary here"
          />
        )}
      </CardContent>
    </Card>
  );
}

function Row({
  label,
  value,
  emphasis = false,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className={emphasis ? "font-bold text-primary" : "font-medium"}>
        {value}
      </span>
    </div>
  );
}
